import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { Table, Select, Tooltip, Input, DatePicker, Form, notification, Modal, Button, Space } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useSelector } from "react-redux";
import { RootState } from "shared/redux/store";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import moment from "moment";
import { ReactComponent as IconError } from '../../assets/images/error.svg';
import { ReactComponent as AddFileGray } from '../../assets/images/upload-file-gray.svg';
import { INRFormatter, numberWithCommas, saveByteArray } from "../../shared/services/Utility";
import API_SERVICE from '../../shared/services/api-service';
import "./index.scss";
import AppUpload from "shared/components/Lead/AppUpload/app-upload";

interface DataType {
  key: string;
  disbursalId: string;
  caseId: string;
  lenderIdentifier: string;
  lenderName: string;
  borrowerName: string;
  loanProductName: string;
  disbursalDate: string;
  disbursedAmount: number;
  insuranceAmount: number;
  status?: string;
  errorDescription?: string;
}

interface Props {
  filterValues: any;
  ref?: any;
  onReconcileDataSequenceComplete?: () => void;
}

const ReconciliationDisbursements = forwardRef<any, Props>(({ filterValues, onReconcileDataSequenceComplete }, ref) => {
  const [statusMap, setStatusMap] = useState<{ [key: string]: string }>({});
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [formType, setFormType] = useState<"reconcile" | "issue" | null>(null);
  const { appColors }: any = useSelector((state: RootState) => state.common);
  const { searchParam } = useSelector((state: RootState) => state.reconciliation);

  // Single form for all records
  const [mainForm] = Form.useForm();

  const [data, setData] = useState<DataType[]>([]);
  const [loading, setLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalData, setTotalData] = useState<{ totalDisbursedAmount: string } | null>(null);

  const [showUpload, setShowUpload] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<any>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [fileList, setFileList] = useState<any[]>([]);

  const loadData = React.useCallback(() => {
    setLoading(true);
    const listPayload = {
      pageNo,
      pageSize,
      searchParam,
      ...filterValues,
    };
    const totalPayload = {
      pageNo,
      pageSize,
      searchParam,
      ...filterValues,
    };
    API_SERVICE.getReconcileDisbursedCases(listPayload)
      .then((res: any) => {
        const content = (res?.data?.payload?.content || []).map((row: any) => ({
          ...row,
          key: row.disbursalId || row.key,
        }));
        setData(content);
        setTotalCount(res?.data?.payload?.totalElements || 0);
        return API_SERVICE.getReconcileTotalCalculation(totalPayload);
      })
      .then((totalRes: any) => {
        if (totalRes?.data) {
          setTotalData(totalRes.data.payload);
        }
      })
      .finally(() => {
        setLoading(false);
        onReconcileDataSequenceComplete?.();
      });
  }, [pageNo, pageSize, searchParam, filterValues, onReconcileDataSequenceComplete]);

  // Call loadData when component mounts or dependencies change
  useEffect(() => {
    loadData();
  }, [loadData]);

  useImperativeHandle(ref, () => ({
    loadData,
    handleExport,
    handleImport,
  }));

  // Utility to format numeric fields for form display
  function formatReconciliationFormValues(record: any) {
    return {
      ...record,
      lenderDisbursementDate: record.disbursalDate ? moment(record.disbursalDate) : null,
      lenderDisbursedAmount: record.disbursedAmount !== undefined ? numberWithCommas(record.disbursedAmount) : '',
      insuranceAmount: record.insuranceAmount !== undefined ? numberWithCommas(record.insuranceAmount) : '',
      revenuePct: record.revenuePercentage !== undefined ? record.revenuePercentage.toString() : '',
      revenueAmount: record.revenueAmount !== undefined ? numberWithCommas(record.revenueAmount) : '',
      // Add more fields as needed
    };
  }

  const handleStatusChange = (key: string, value: string) => {
    // Close any existing editing row first
    if (editingKey !== null && editingKey !== key) {
      // Always reset status to original data for previous editing row
      setStatusMap((prev) => {
        const updated = { ...prev };
        // Reset to the original status from data array
        const prevRecord = data.find(item => item.key === editingKey);
        if (prevRecord && prevRecord.status) {
          updated[editingKey] = prevRecord.status;
        } else {
          delete updated[editingKey];
        }
        return updated;
      });
      mainForm.resetFields();
      setEditingKey(null);
      setFormType(null);
    }

    setStatusMap((prev) => ({ ...prev, [key]: value }));

    // Begin editing the selected row
    if (value === "Reconciled") {
      startEdit(key, "reconcile");
    } else if (value === "Issue") {
      startEdit(key, "issue");
    }
  };

  const startEdit = (key: string, type: "reconcile" | "issue") => {
    const record = data.find(item => item.key === key);
    if (!record) return;

    // Set editing state
    setEditingKey(key);
    setFormType(type);

    // Reset form before setting values
    mainForm.resetFields();

    if (type === "reconcile") {
      // Use formatted values for form display
      const formattedValues = formatReconciliationFormValues({
        disbursalDate: record.disbursalDate,
        disbursedAmount: record.disbursedAmount,
        insuranceAmount: record.insuranceAmount,
        // Other fields will be empty by default
      });
      mainForm.setFieldsValue(formattedValues);
    } else {
      // For issue form, we just reset to empty
      mainForm.resetFields();
    }
  };

  const cancelEdit = () => {
    // Only clear status if it's not a saved row
    if (editingKey && !data.find(item => item.key === editingKey)?.status) {
      setStatusMap((prev) => {
        const updated = { ...prev };
        delete updated[editingKey];
        return updated;
      });
    }

    // Clear editing state
    setEditingKey(null);
    setFormType(null);

    // Reset form
    mainForm.resetFields();
  };

  const saveEdit = async () => {
    if (!editingKey) return;

    try {
      const values = await mainForm.validateFields();
      const record = data.find(item => item.key === editingKey);
      if (!record) return;

      // Clean values (remove commas from formatted numbers)
      const cleanValues = {
        ...values,
        lenderDisbursedAmount: values.lenderDisbursedAmount ? Number(values.lenderDisbursedAmount.replace(/,/g, '')) : undefined,
        insuranceAmount: values.insuranceAmount ? Number(values.insuranceAmount.replace(/,/g, '')) : undefined,
        revenueAmount: values.revenueAmount ? Number(values.revenueAmount.replace(/,/g, '')) : undefined,
        revenuePercentage: values.revenuePct ? Number(values.revenuePct) : undefined,
      };

      // Format dates for the API
      const formattedInvoiceDate = values.invoiceDate ? moment(values.invoiceDate).format('YYYY-MM-DD') : undefined;
      const formattedLenderDisbursementDate = values.lenderDisbursementDate ? moment(values.lenderDisbursementDate).format('YYYY-MM-DD') : undefined;

      // Get the invoice number and date values
      const invoiceNo = mainForm.getFieldValue('invoiceNo');
      const invoiceDate = mainForm.getFieldValue('invoiceDate');
      
      // Validation: If Invoice No. is entered but Invoice Date is missing, show error
      if (invoiceNo && !invoiceDate) {
        // Apply errors to the appropriate fields
        const fieldErrors: { name: string; errors: string[] }[] = [];
        
        // If invoice no is provided but date is missing
        fieldErrors.push({
          name: 'invoiceDate',
          errors: ['Required']
        });
        
        mainForm.setFields(fieldErrors);
        return;
      }
      
      // Validation: If Invoice Date is entered but Invoice No. is missing, show error
      if (!invoiceNo && invoiceDate) {
        // Apply errors to the appropriate fields
        const fieldErrors: { name: string; errors: string[] }[] = [];
        
        // If invoice date is provided but invoice no is missing
        fieldErrors.push({
          name: 'invoiceNo',
          errors: ['Required']
        });
        
        mainForm.setFields(fieldErrors);
        return;
      }
      
      // Prepare payload as per the required structure
      const payload = {
        disbursalId: record.disbursalId,
        lenderDisbursementDate: formattedLenderDisbursementDate,
        lenderDisbursementAmount: cleanValues.lenderDisbursedAmount,
        insuranceAmount: cleanValues.insuranceAmount,
        revenuePercentage: cleanValues.revenuePercentage,
        revenueAmount: cleanValues.revenueAmount,
        // Only include invoiceNo if it has a value
        ...(values.invoiceNo ? { invoiceNo: values.invoiceNo } : {}),
        // Only include invoiceDate if it has a value
        ...(formattedInvoiceDate ? { invoiceDate: formattedInvoiceDate } : {}),
        reconcileStatus: "RECONCILED"
      };

      // Send data to API
      setLoading(true);
      API_SERVICE.updateReconciliationDisbursal(payload)
        .then((res: any) => {
          // Mark this row as having saved data
          setStatusMap((prev) => ({ ...prev, [record.key]: "Reconciled" }));
          
          // Clear editing state
          setEditingKey(null);
          setFormType(null);
          
          // Refresh the data
          loadData();
          
          notification.success({ message: "Reconciliation saved successfully." });
        })
        .catch((error: any) => {
          notification.error({ message: "Failed to save reconciliation." });
        })
        .finally(() => {
          setLoading(false);
        });
    } catch (err) {
      // Validation error
    }
  };

  const saveIssue = async () => {
    if (!editingKey) return;

    try {
      const values = await mainForm.validateFields();
      const record = data.find(item => item.key === editingKey);
      if (!record) return;

      // Process form data
      const issueType = values.issueType;
      const issueReason = issueType;
      const issueComment = values.otherReason || '';

      // Prepare payload as per the required structure
      const payload = {
        disbursalId: record.disbursalId,
        reconcileStatus: "ISSUE",
        issueType: issueType,
        issueReason: issueReason,
        issueComment: issueType === "Others" ? issueComment : undefined
      };

      // Send data to API
      setLoading(true);
      API_SERVICE.updateReconciliationDisbursal(payload)
        .then((res: any) => {
          // Update status in local state
          setStatusMap((prev) => ({ ...prev, [record.key]: "Issue" }));
          
          // Clear editing state
          setEditingKey(null);
          setFormType(null);
          
          // Refresh the data
          loadData();
          
          notification.success({ message: "Issue reported successfully." });
        })
        .catch((error: any) => {
          notification.error({ message: "Failed to report issue." });
        })
        .finally(() => {
          setLoading(false);
        });
    } catch (err) {
      // Validation error
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys: React.Key[]) => setSelectedRowKeys(newSelectedRowKeys),
    preserveSelectedRowKeys: true,
  };

  const columns: ColumnsType<DataType> = [
    {
      title: 'Disbursal ID',
      dataIndex: 'disbursalId',
      key: 'disbursalId',
      fixed: 'left',
      render: (value: string) => value ?? '',
    },
    {
      title: 'Case ID',
      dataIndex: 'caseId',
      key: 'caseId',
      fixed: 'left',
      render: (value: string) => value ?? '',
    },
    {
      title: 'Lender Identifier',
      dataIndex: 'lenderIdentifier',
      key: 'lenderIdentifier',
      render: (value: string) => value ?? '',
    },
    {
      title: 'Lender',
      dataIndex: 'lenderName',
      key: 'lenderName',
      render: (value: string) => value ?? '',
    },
    {
      title: 'Customer Name',
      dataIndex: 'borrowerName',
      key: 'borrowerName',
      render: (value: string) => value ?? '',
    },
    {
      title: 'Product',
      dataIndex: 'loanProductName',
      key: 'loanProductName',
      render: (value: string) => value ?? '',
    },
    {
      title: 'Disbursement Date',
      dataIndex: 'disbursalDate',
      key: 'disbursalDate',
    },
    {
      title: 'Disbursement Amount',
      dataIndex: 'disbursedAmount',
      key: 'disbursedAmount',
      render: (value: number) => value !== null && value !== undefined ? INRFormatter(value) : '',
    },
    {
      title: 'Insurance Amount',
      dataIndex: 'insuranceAmount',
      key: 'insuranceAmount',
      render: (value: number) => value !== null && value !== undefined ? INRFormatter(value) : '',
    },
    {
      title: 'Sales user hierarchy',
      dataIndex: 'salesUserHierarchy',
      key: 'salesUserHierarchy',
      width: 150,
      ellipsis: true,
      render: (value: any) =>(
        <Tooltip title={value}>
          {value}
        </Tooltip>
      )
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => {
        return (
          <Select
            value={statusMap[record.key] || undefined}
            style={{ width: 120 }}
            onChange={(value) => handleStatusChange(record.key, value)}
            placeholder="Select Status"
            size="small"
            dropdownMatchSelectWidth={false}
          >
            <Select.Option value="Reconciled">Reconciled</Select.Option>
            <Select.Option value="Issue">Issue</Select.Option>
          </Select>
        );
      },
    },
    {
      title: 'Rectify',
      key: 'rectify',
      align: 'center',
      render: (_: any, record: any) =>
        record?.errorDescription?.length > 0 && (
          <Tooltip placement="topRight" title={record?.errorDescription}>
            <IconError width={20} height={20} />
          </Tooltip>
        ),
    },
  ];

  const renderEditableRow = (record: DataType) => {
    // Validation functions for form fields
    const validateNumber = (_: any, value: string) => {
      if (value && !/^\d*$/.test(value.replace(/,/g, ''))) {
        return Promise.reject('Please enter numbers only');
      }
      return Promise.resolve();
    };

    const validatePercentage = (_: any, value: string | number) => {
      if (value === undefined || value === null || value === '') return Promise.resolve();
      const numValue = typeof value === 'number' ? value : Number(value.toString().replace(/,/g, ''));
      if (isNaN(numValue) || numValue < 0 || numValue > 100) {
        return Promise.reject('Please enter a value between 0 and 100');
      }
      return Promise.resolve();
    };

    // Handle amount input formatting
    const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>, fieldName: string) => {
      const rawValue = e.target.value.replace(/[^\d]/g, '');
      if (rawValue) {
        mainForm.setFieldsValue({
          [fieldName]: numberWithCommas(rawValue)
        });
      } else {
        mainForm.setFieldsValue({
          [fieldName]: ''
        });
      }
    };

    // Handle percentage input (0-100)
    const handlePercentageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.replace(/[^\d]/g, '');

      if (value) {
        const numValue = Number(value);
        const limitedValue = Math.min(100, numValue);
        mainForm.setFieldsValue({
          revenuePct: limitedValue.toString()
        });
      } else {
        mainForm.setFieldsValue({
          revenuePct: ''
        });
      }
    };

    return (
      <div className="reconciliation-edit-row">
        <Form form={mainForm} layout="inline" className="edit-form-inline material-ui-style">
          <Form.Item label="Lender Disbursement Date" name="lenderDisbursementDate" rules={[{ required: true, message: "Required" }]}>
            <DatePicker format="DD/MM/YYYY" size="small" className="reconciliation-field" />
          </Form.Item>

          <Form.Item
            label="Lender Disbursed amount"
            name="lenderDisbursedAmount"
            rules={[
              { required: true, message: "Required" },
              { validator: validateNumber }
            ]}
          >
            <Input
              placeholder="Rs."
              size="small"
              className="reconciliation-field"
              value={mainForm.getFieldValue('lenderDisbursedAmount')}
              onChange={(e) => handleAmountChange(e, 'lenderDisbursedAmount')}
            />
          </Form.Item>

          <Form.Item
            label="Insurance amount"
            name="insuranceAmount"
            rules={[
              { required: true, message: "Required" },
              { validator: validateNumber }
            ]}
          >
            <Input
              placeholder="Rs."
              size="small"
              className="reconciliation-field"
              value={mainForm.getFieldValue('insuranceAmount')}
              onChange={(e) => handleAmountChange(e, 'insuranceAmount')}
            />
          </Form.Item>

          <Form.Item
            label="Revenue %"
            name="revenuePct"
            rules={[{ validator: validatePercentage }]}
          >
            <Input
              size="small"
              className="reconciliation-field"
              placeholder="%"
              value={mainForm.getFieldValue('revenuePct')}
              onChange={handlePercentageChange}
            />
          </Form.Item>

          <Form.Item
            label="Revenue amount"
            name="revenueAmount"
            rules={[{ validator: validateNumber }]}
          >
            <Input
              placeholder="Rs."
              size="small"
              className="reconciliation-field"
              onChange={(e) => handleAmountChange(e, 'revenueAmount')}
            />
          </Form.Item>

          <Form.Item 
            label="Invoice No." 
            name="invoiceNo" 
            validateTrigger={["onChange", "submit"]}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const dateValue = getFieldValue('invoiceDate');
                  
                  // Only check for validation when both values are relevant for comparison
                  if (!value && !dateValue) {
                    return Promise.resolve(); // Both are empty, no validation needed
                  }
                  
                  // If invoice date has value but this field is empty/missing
                  if (!value && dateValue) {
                    return Promise.reject('Required');
                  }
                  
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Input 
              size="small" 
              className="reconciliation-field" 
              placeholder="Invoice No." 
              onChange={(e) => {
                // Only clear errors if the field has a value
                if (e.target.value) {
                  // Clear the error from invoice date field if it exists
                  const dateField = mainForm.getFieldError('invoiceDate');
                  if (dateField && dateField.length > 0) {
                    mainForm.setFields([{
                      name: 'invoiceDate',
                      errors: []
                    }]);
                  }
                }
              }}
            />
          </Form.Item>

          <Form.Item 
            label="Invoice Date" 
            name="invoiceDate"
            validateTrigger={["onChange", "submit"]}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const invoiceNo = getFieldValue('invoiceNo');
                  
                  // Only check for validation when both values are relevant for comparison
                  if (!value && !invoiceNo) {
                    return Promise.resolve(); // Both are empty, no validation needed
                  }
                  
                  // If invoice no has value but this field is missing
                  if (!value && invoiceNo) {
                    return Promise.reject('Required');
                  }
                  
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <DatePicker 
              format="DD/MM/YYYY" 
              placeholder="Select date" 
              size="small" 
              className="reconciliation-field"
              onChange={(date) => {
                // Only clear errors if a date is selected
                if (date) {
                  // Clear the error from invoice no field if it exists
                  const noField = mainForm.getFieldError('invoiceNo');
                  if (noField && noField.length > 0) {
                    mainForm.setFields([{
                      name: 'invoiceNo',
                      errors: []
                    }]);
                  }
                }
              }}
            />
          </Form.Item>

          <div className="action-buttons-container">
            <CheckCircleOutlined onClick={saveEdit} className="action-check-icon" />
            <CloseCircleOutlined onClick={cancelEdit} className="action-close-icon" />
          </div>
        </Form>
      </div>
    );
  };

  const renderIssueRow = (record: DataType) => {
    return (
      <div className="reconciliation-edit-row">
        <Form form={mainForm} layout="inline" className="edit-form-inline material-ui-style">
          <Form.Item
            label="Issue Type"
            name="issueType"
            rules={[{ required: true, message: "Required" }]}
          >
            <Select
              size="small"
              className="reconciliation-field"
              placeholder="Select Issue Type"
              onChange={(value) => {
                // Reset otherReason if the type is not "Others"
                if (value !== "Others") {
                  mainForm.setFieldsValue({ otherReason: undefined });
                }
              }}
            >
              <Select.Option value="Not Found In Lender MIS">Not found in lender MIS</Select.Option>
              <Select.Option value="Amount Mismatch">Amount mismatch</Select.Option>
              <Select.Option value="Others">Others</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.issueType !== currentValues.issueType}
          >
            {({ getFieldValue }) =>
              getFieldValue('issueType') === 'Others' ? (
                <Form.Item
                  label="Specify Issue"
                  name="otherReason"
                  rules={[
                    { required: true, message: "Please specify the issue" },
                    { max: 100, message: "Maximum 100 characters allowed" }
                  ]}
                >
                  <Input
                    size="small"
                    className="reconciliation-field"
                    style={{ width: 300 }}
                    placeholder="( max character - 100 )"
                    maxLength={100}
                  />
                </Form.Item>
              ) : null
            }
          </Form.Item>

          <div className="action-buttons-container">
            <CheckCircleOutlined onClick={saveIssue} className="action-check-icon" />
            <CloseCircleOutlined onClick={cancelEdit} className="action-close-icon" />
          </div>
        </Form>
      </div>
    );
  };

  // Export handler
  const handleExport = async () => {
    // Check if rows are selected
    if (!selectedRowKeys || selectedRowKeys.length === 0) {
      notification.info({ message: "Select the cases to export" });
      return;
    }
    
    setLoading(true);
    try {
      const payload: any = {
        disbursalIds: selectedRowKeys.map(key => String(key))
      };

      const response = await API_SERVICE.exportReconcileDisbursals(payload);
      const filename = response.headers["content-disposition"]?.split("filename=")?.[1] || "reconciliation-disbursements.xlsx";
      saveByteArray([response.data], filename);
      notification.success({ message: "File downloaded successfully" });
    } catch (error) {
      notification.error({ message: "File download failed" });
    } finally {
      setLoading(false);
    }
  };

  // Import handler
  const handleImport = () => {
    setUploadedFile(null);
    setShowUpload(true);
  };

  const uploadedDocumentFile = async () => {
    if (!uploadedFile) {
      notification.error({ message: "Please upload .xlsx file!" });
      return;
    }
    setImportLoading(true);
    try {
      const entityType = "RECONCILE_DISBURSAL";
      const formData = new FormData();
      formData.append("file", uploadedFile);
      const res = await API_SERVICE.bulkUploadStart(entityType, formData);
      notification.success({ message: res?.data?.message || "Upload started" });
      // Execute upload
      await API_SERVICE.bulkUploadExecute(res?.data?.payload?.uploadId, entityType);
      notification.success({ message: "Import completed successfully" });
      setShowUpload(false);
      setUploadedFile(null);
      loadData(); // Refresh table after successful import
    } catch (e: any) {
      notification.error({ message: API_SERVICE.handleErrors(e) });
    } finally {
      setImportLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    handleExport,
    handleImport,
    loadData,
  }));

  return (
    <div className="reconciliation-table-wrapper reconciliation-material-ui">
      <Table
        rowKey="disbursalId"
        rowSelection={rowSelection}
        className="dynamic-pagination-checkbox table-full-width reconciliation-tight-table reconciliation-table-with-footer"
        style={{ '--bgColor': appColors?.appPrimaryColor ?? '' } as React.CSSProperties}
        scroll={{ x: 'max-content' }}
        size="small"
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={{
          showSizeChanger: true,
          position: ['bottomRight'],
          itemRender: (page, type, originalElement) => originalElement,
          current: pageNo,
          pageSize: pageSize,
          total: totalCount,
          onChange: (page, size) => {
            setPageNo(page);
            setPageSize(size || 10);
          },
        }}
        expandable={{
          expandedRowRender: (record) => {
            if (formType === "reconcile") {
              return renderEditableRow(record);
            } else if (formType === "issue") {
              return renderIssueRow(record);
            }
            return null;
          },
          expandedRowKeys: editingKey ? [editingKey] : [],
          rowExpandable: (record) => record.key === editingKey,
          expandIconColumnIndex: -1 // This hides the expand icon column
        }}
        footer={() => (
          <div className="reconciliation-table-custom-footer">
            <div className="reconciliation-table-footer-cell reconciliation-table-footer-total">
              Total Disbursed Amount: {INRFormatter(Number(totalData?.totalDisbursedAmount || '0'))}
            </div>
          </div>
        )}
      />
      <Modal
        width={300}
        visible={showUpload}
        style={{ marginBottom: 230 }}
        okText="Yes"
        cancelText="No"
        title="Upload Excel"
        centered
        closable={false}
        maskClosable={false}
        footer={false}
      >
        <AppUpload
          acceptType={['xlsx']}
          callBack={(file) => setUploadedFile(file?.file || file)}
          setFileSize={() => {}}
          setUploadedFile={setUploadedFile}
          setIsSelectedLenderDoc={() => {}}
          showUploadList={uploadedFile != null}
          disabled={importLoading}
          multiple={false}
          maxSize={10}
          className="upload-wrapper"
        >
          <AddFileGray />Upload Excel File
        </AppUpload>
        <Space direction="vertical" style={{ marginBottom: "15px" }}>
          <Button
            type="primary"
            className="export-button dynamic-btn-primary"
            style={{ width: "250px" }}
            loading={importLoading}
            onClick={uploadedDocumentFile}
          >
            Upload
          </Button>
          <Button
            onClick={() => {
              setShowUpload(false);
              setFileList([]);
              setUploadedFile(null);
            }}
            className="export-button dynamic-btn-default"
            style={{ width: "250px" }}
          >
            Close
          </Button>
        </Space>
      </Modal>
    </div>
  );
});

export default ReconciliationDisbursements;